import { useState, useEffect, useCallback, useRef } from 'react';
import useForm from '@base/Form/useForm';
import { useSnackbar } from '@stores/snackbar';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import fetch from '@api/fetchers';
import { getFullDateWithDayAtStart } from '@utils/dates';
import useCountries from '@api/hooks/useCountries';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Me from '@models/Me';

import { getIsLoggedIn, getMePhone, getFullName } from '@api/selectors/auth';
import {
  getSetLocalPhoneNumber,
  getSetLocalEmailAndName,
  getIsRegistered
} from '@stores/selectors/login';
import { getMeLatestUserSubscriptions } from '@api/selectors/subscription';
import useLoginStore from '@stores/login/loginStore';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import styled from '@emotion/styled';
import { getSubscriptionType } from '@Analytics/Segment';
import PersonalDetails from './PersonalDetails';
import GoalDetails from './GoalDetails';

import {
  getEnquiryFormEndpoint,
  getGoalSpecificCityCentreEndpoint
} from '../apiHelper';
import {
  FORM_ENQUIRY_FORM_VIEWED,
  FORM_ENQUIRY_FORM_SUBMITTED,
  FORM_ENQUIRY_FORM_CONTINUE,
  DEFAULT_COUNTRY,
  goalDetailsData
} from '../constants';

import {
  Wrapper,
  StyledPaper,
  FormTitle,
  FormContainer,
  AcceptTerms,
  TermsContent,
  TermsLink,
  TermsText,
  StyP2,
  SubmitEnquiryButon,
  CounsellorPaper,
  HR,
  CounsellorNameWrapper,
  CounsellorName
} from './styles';

const formState = {
  name: '',
  phone: '',
  dob: '',
  gender: '',
  goal: '',
  class: '',
  centre: '',
  year: '',
  counsellor: '',
  referral: '',
  about: ''
};

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EnquiryForm = ({
  cmsData,
  offlineGoals,
  counsellorName,
  counsellorEmail,
  formType,
  showThankyouPage
}) => {
  const setLocalPhoneNumber = useLoginStore(getSetLocalPhoneNumber);
  const setLocalEmailAndName = useLoginStore(getSetLocalEmailAndName);
  const { open: showSnackbar } = useSnackbar();

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isValidating } = useMyInfo();
  const plusSubscriptions = useMyInfo(getMeLatestUserSubscriptions);
  const activeSubscription =
    Me.getActiveSubscriptionToAnyGoal(plusSubscriptions) ?? {};

  const { timeRemaining = 0 } = activeSubscription;
  const default_is_subscription_active = timeRemaining > 0;
  let subscription = {};
  if (default_is_subscription_active) {
    subscription = activeSubscription.subscription;
  }
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [payload, setPayload] = useState(null);
  const countries = useCountries();
  const defaultPhoneNumber = useMyInfo(getMePhone);
  const defaultName = useMyInfo(getFullName);
  formState.counsellor = counsellorName?.username ?? '';
  const isRegistered = useLoginStore(getIsRegistered);

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    goalDetailsData.Year.options = cmsData?.enquiryForm?.year;
    goalDetailsData.Goal.options = offlineGoals;
    goalDetailsData.Centre.options = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [defaultFormState, setDefaultFormState] = useState(formState);
  const [goalDetails, setGoalDetails] = useState(goalDetailsData);

  const { registrar, isSubmitting, onFormSubmit } = useForm({
    data: defaultFormState
  });

  useEffect(() => {
    setShowLoader(isValidating);
  }, [isValidating]);

  useEffect(() => {
    const formStateWithNumber = {
      ...defaultFormState,
      phone: defaultPhoneNumber,
      name: defaultName
    };
    setDefaultFormState(formStateWithNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPhoneNumber, defaultName]);
  const getCentresDataOfAGoal = async (goalUID) => {
    const cityCentreList = await fetch(
      getGoalSpecificCityCentreEndpoint(goalUID.toUpperCase())
    );
    const cityCentreData = [];
    cityCentreList?.forEach((city) => {
      city.centres.forEach((centre, index) => {
        cityCentreData.push({
          id: `${city.id}${index}`,
          label: `${city.name} - ${centre.name}`,
          value: centre.uid
        });
      });
    });
    return cityCentreData;
  };

  useEffect(() => {
    const selectedGoalUID = registrar.data.Goal?.value?.toLowerCase();
    if (selectedGoalUID) {
      const goalDetailsInfo = { ...goalDetailsData };
      const availableClassesOptions =
        cmsData.enquiryForm?.class[selectedGoalUID] ?? [];
      goalDetailsInfo.Class.options = availableClassesOptions;
      goalDetailsInfo.Class.isHidden = !availableClassesOptions.length;

      getCentresDataOfAGoal(selectedGoalUID).then((centreOptions) => {
        goalDetailsInfo.Centre.options = centreOptions;
        goalDetailsInfo.Centre.isHidden = !centreOptions.length;
        setGoalDetails(goalDetailsInfo);
      });

      registrar.setData('Centre', undefined);
      registrar.setData('Class', undefined);
      registrar.setData('Year', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsData?.enquiryForm?.class, registrar.data?.Goal?.value]);

  const [country, setCountry] = useState(
    countries.length
      ? countries.find((c) => c.code === DEFAULT_COUNTRY.code)
      : DEFAULT_COUNTRY
  );

  useEffect(() => {
    segmentStorage.setItem('lps', 'Enquiry Form');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUserDataLoaded = useMyInfo(getIsLoggedIn);
  const { isLoading: isUserDataLoading, isValidating: userDataNeedsLoading } =
    useMyInfo();

  const isUserLoggedIn = !isUserDataLoading && isUserDataLoaded;
  const isUserLoggedOut = !userDataNeedsLoading && !isUserDataLoaded;

  useEffect(() => {
    if (isUserLoggedIn || isUserLoggedOut)
      segment.track(FORM_ENQUIRY_FORM_VIEWED, {
        promoter_name: counsellorName
          ? `${counsellorName.first_name} ${counsellorName.last_name}`
          : '',
        promoter_username: counsellorName ? counsellorName.username : '',
        form_type: formType || '',
        user_subscription_plan: getSubscriptionType(subscription?.type)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, isUserLoggedIn, subscription.type]);

  const changeCountry = (countryOption) => {
    setCountry(countries.find((c) => c.code === countryOption.value));
  };

  const submitEnquiryData = useCallback(() => {
    fetch(getEnquiryFormEndpoint(), {
      requestType: 'POST',
      body: { data: payload }
    })
      .then(() => {
        showThankyouPage(true);
        segment.track(FORM_ENQUIRY_FORM_SUBMITTED, {
          class: payload.class,
          gender: payload.gender,
          goal_uid: payload.goal,
          goal_name: payload.goal_name,
          centre: payload.centre_name || '',
          target_year: payload.year ?? '',
          promoter_name: counsellorName
            ? `${counsellorName.first_name} ${counsellorName.last_name}`
            : '',
          promoter_username: payload.counsellor,
          learner_name: payload.name,
          referral_code: payload.referral_code,
          about_unacademy: payload.about_unacademy,
          form_type: formType,
          user_subscription_plan: getSubscriptionType(subscription?.type),
          counsellor_email: counsellorEmail || ''
        });
      })
      .catch(() => {
        showSnackbar({
          message: 'Something went wrong, Try again later',
          type: 'error'
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    if (isLoggedIn && payload) {
      submitEnquiryData();
    }
  }, [isLoggedIn, payload, submitEnquiryData]);
  useEffect(() => {
    if (isRegistered) {
      setLocalEmailAndName('', payload?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegistered]);

  const referralInputRef = useRef(null);

  const onSubmit = (data) => {
    const goalDetailsKeys = Object.keys(goalDetails ?? {});
    const goalDetailData = {};
    goalDetailsKeys.forEach((goalDetailsKey) => {
      goalDetailData[goalDetailsKey.toLowerCase()] =
        data[goalDetailsKey]?.value;
    });
    setPayload({
      name: data.name,
      phone: data.phone,
      dob: data?.dob ?? getFullDateWithDayAtStart(data.dob),
      gender: data.gender.value,
      counsellor: data.counsellor,
      ...goalDetailData,
      year: goalDetailData.year || '',
      goal_name: data.Goal.label,
      referral_code: referralInputRef.current?.value || data.referral,
      about_unacademy: data.about.value,
      centre_name: data.Centre?.label || ''
    });

    if (!isLoggedIn) {
      setLocalPhoneNumber(String(data.phone), DEFAULT_COUNTRY);
      setLocalEmailAndName('', data?.name);
      openLoginDrawer();
    }

    segment.track(FORM_ENQUIRY_FORM_CONTINUE, {
      class: goalDetailData.class,
      gender: data.gender.value,
      goal_uid: goalDetailData.goal,
      goal_name: data.Goal.label,
      centre: data.Centre?.label || '',
      target_year: goalDetailData.year || '',
      promoter_name: counsellorName
        ? `${counsellorName.first_name} ${counsellorName.last_name}`
        : '',
      promoter_username: data.counsellor,
      learner_name: data.name,
      form_type: formType,
      user_subscription_plan: getSubscriptionType(subscription?.type),
      counsellor_email: counsellorEmail || ''
    });
  };
  const showExtraFields = formType !== 'content_sales';
  const handleOnSubmit = onFormSubmit(onSubmit);

  if (showLoader) {
    return (
      <LoaderWrapper>
        <CircularLoader />
      </LoaderWrapper>
    );
  }
  return (
    <Wrapper>
      <StyledPaper>
        <FormTitle>Register with Unacademy</FormTitle>

        <FormContainer>
          <PersonalDetails
            registrar={registrar}
            changeCountry={changeCountry}
            country={country}
            countries={countries}
            showExtraFields={showExtraFields}
          />

          <GoalDetails
            registrar={registrar}
            fields={goalDetails}
            knowAbout={cmsData?.enquiryForm?.about}
            showExtraFields={showExtraFields}
            referralInputRef={referralInputRef}
          />
        </FormContainer>
        <AcceptTerms>
          <TermsContent>
            <StyP2>By continuing, you agree to</StyP2>
            <TermsLink target="_blank" href="/terms">
              <TermsText>&nbsp;Unacademy’s terms</TermsText>
            </TermsLink>
          </TermsContent>
        </AcceptTerms>
        <SubmitEnquiryButon
          onClick={handleOnSubmit}
          width="100%"
          variant={isSubmitting ? 'disabled' : 'filled'}
          label="Continue"
          color="black"
        />
      </StyledPaper>
      {counsellorEmail && (
        <CounsellorPaper>
          <HR />
          <CounsellorNameWrapper>
            Counsellor email &nbsp;
            <CounsellorName>{counsellorEmail}</CounsellorName>
          </CounsellorNameWrapper>
        </CounsellorPaper>
      )}
    </Wrapper>
  );
};

export default EnquiryForm;
